import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NavBar } from '../components/NavBar';
import {
  Loader2, ChevronLeft, ChevronRight, Shuffle, Star, ShoppingCart, Flame, Pencil, Target,
  Calendar, Utensils, Sparkles, Quote, Leaf, AlertCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { MealSwapDialog } from '../components/nutrition/MealSwapDialog';
import { FavoriteMealsSection } from '../components/nutrition/FavoriteMealsSection';
import { MealRawTextEditorDialog } from '../components/nutrition/MealRawTextEditorDialog';

const dietLabels = {
  vegetarian: 'Vegetarian', vegan: 'Vegan', pescatarian: 'Pescatarian',
  gluten_free: 'Gluten-Free', dairy_free: 'Dairy-Free', nut_free: 'Nut Allergy',
  keto: 'Keto', halal: 'Halal', kosher: 'Kosher'
};

export const NutritionPlanViewerPage = () => {
  const { planId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [week, setWeek] = useState(1);
  const [favorites, setFavorites] = useState([]);
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  const [swapTarget, setSwapTarget] = useState(null);
  const [favoritingMeal, setFavoritingMeal] = useState(null);
  const [removingFavorite, setRemovingFavorite] = useState(null);
  const [generatingGrocery, setGeneratingGrocery] = useState(false);
  const [groceryList, setGroceryList] = useState(null);
  // Raw-text editor (per-meal free-form override)
  const [rawTextTarget, setRawTextTarget] = useState(null); // { weekIndex, mealIndex, initialText, mealLabel } | null

  const fetchData = useCallback(async () => {
    try {
      const [p, f] = await Promise.all([
        api().get(`/nutrition-plans/view/${planId}`),
        api().get('/nutrition-plans/favorites/list'),
      ]);
      setPlan(p.data.plan);
      setFavorites(f.data.favorites || []);
      try {
        const g = await api().get(`/nutrition-plans/grocery-list/${planId}`);
        if (g.data?.grocery_list) setGroceryList(g.data.grocery_list);
      } catch { /* no grocery yet */ }
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('Nutrition plans are exclusive to premium members.');
        navigate('/subscription');
        return;
      }
      toast.error('Failed to load plan');
    }
    setLoading(false);
  }, [api, planId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const mealSignature = (foods) => (foods || '').trim().toLowerCase().slice(0, 200);
  const isMealFavorited = (foods) => favorites.some((f) => f.signature === mealSignature(foods));

  const toggleFavorite = async (meal) => {
    if (!meal?.foods) return;
    const existing = favorites.find((f) => f.signature === mealSignature(meal.foods));
    if (existing) {
      setRemovingFavorite(existing.id);
      try {
        await api().delete(`/nutrition-plans/favorites/${existing.id}`);
        setFavorites((prev) => prev.filter((f) => f.id !== existing.id));
      } catch { toast.error('Failed to remove'); }
      setRemovingFavorite(null);
      return;
    }
    setFavoritingMeal(mealSignature(meal.foods));
    try {
      const res = await api().post('/nutrition-plans/favorites', { meal });
      if (res.data?.favorite) setFavorites((prev) => [res.data.favorite, ...prev]);
      toast.success('Added to favorites');
    } catch { toast.error('Failed to save'); }
    setFavoritingMeal(null);
  };

  const removeFavorite = async (favId) => {
    setRemovingFavorite(favId);
    try {
      await api().delete(`/nutrition-plans/favorites/${favId}`);
      setFavorites((prev) => prev.filter((f) => f.id !== favId));
    } catch { toast.error('Failed to remove'); }
    setRemovingFavorite(null);
  };

  const generateGrocery = async () => {
    setGeneratingGrocery(true);
    try {
      const res = await api().post(`/nutrition-plans/grocery-list/${planId}`, { weeks: 1 });
      setGroceryList(res.data.grocery_list);
      toast.success('Grocery list generated!');
    } catch {
      toast.error('Failed to generate grocery list');
    }
    setGeneratingGrocery(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <NavBar />
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-12 w-12 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen bg-black">
        <NavBar />
        <div className="text-center py-20">
          <p className="text-white">Plan not found</p>
          <Link to="/nutrition-plans/my" className="text-primary underline mt-2 inline-block">Back to my plans</Link>
        </div>
      </div>
    );
  }

  const currentWeek = plan.weeks?.[week - 1];
  const totalWeeks = plan.weeks?.length || plan.duration_weeks || 1;

  return (
    <div className="min-h-screen bg-black" data-testid="nutrition-viewer-page">
      <NavBar />

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-4xl">
        {/* Hero header */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="relative mb-6 rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-900/80 via-black/70 to-zinc-900/60 shadow-[0_8px_30px_rgba(0,0,0,0.35)] overflow-hidden"
        >
          {/* Ambient glow */}
          <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-primary/5 blur-3xl" />

          <div className="relative p-5 sm:p-7">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary bg-primary/[0.08] border border-primary/25 rounded-full px-2.5 py-1 mb-3">
              <Sparkles className="h-3 w-3" /> AI Nutrition Plan
            </div>

            <h1
              className="font-heading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-wider leading-[1.05] bg-gradient-to-b from-white to-white/70 bg-clip-text text-transparent"
              data-testid="plan-title"
            >
              {plan.program_title}
            </h1>

            {/* Meta chips */}
            <div className="flex flex-wrap gap-1.5 mt-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-white/90 bg-white/[0.04] border border-white/10 rounded-full px-2.5 py-1">
                <Calendar className="h-3 w-3 text-primary" /> {plan.duration_weeks} weeks
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-white/90 bg-white/[0.04] border border-white/10 rounded-full px-2.5 py-1">
                <Utensils className="h-3 w-3 text-primary" /> {plan.overview?.meals_per_day || 'custom'} meals/day
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-white/90 bg-white/[0.04] border border-white/10 rounded-full px-2.5 py-1">
                <Flame className="h-3 w-3 text-orange-400" />
                {plan.overview?.daily_calories_start}
                {plan.overview?.daily_calories_end &&
                  plan.overview.daily_calories_end !== plan.overview.daily_calories_start &&
                  `–${plan.overview.daily_calories_end}`}
                <span className="text-muted-foreground">kcal</span>
              </span>
            </div>

            {/* Strategy blockquote */}
            {plan.overview?.strategy && (
              <div className="mt-5 relative pl-4 sm:pl-5">
                <span className="absolute left-0 top-0 bottom-0 w-[2px] rounded-full bg-gradient-to-b from-primary/60 via-primary/25 to-transparent" />
                <Quote className="absolute -left-1 -top-1 h-4 w-4 text-primary/40" />
                <p className="text-white/80 text-sm sm:text-[15px] leading-relaxed italic pl-3">
                  {plan.overview.strategy}
                </p>
              </div>
            )}

            {/* Dietary chips */}
            {(() => {
              const isJunk = (v) => {
                if (!v) return true;
                const s = String(v).trim().toLowerCase();
                return s === '' || s === 'none' || s.startsWith('test_cookie_auth');
              };
              const cleanRestrictions = (plan.dietary_restrictions || []).filter((r) => !isJunk(r));
              const cleanNotes = isJunk(plan.custom_dietary_notes) ? '' : plan.custom_dietary_notes;
              if (cleanRestrictions.length === 0 && !cleanNotes) return null;
              return (
                <div className="mt-5 pt-4 border-t border-white/[0.06] flex flex-wrap items-center gap-1.5">
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-300/80 mr-1">
                    <Leaf className="h-3 w-3" /> Dietary
                  </span>
                  {cleanRestrictions.map((r) => (
                    <span
                      key={r}
                      className="text-[11px] font-medium text-emerald-300 bg-emerald-500/[0.08] border border-emerald-500/25 rounded-full px-2.5 py-0.5"
                    >
                      {dietLabels[r] || r}
                    </span>
                  ))}
                  {cleanNotes && (
                    <span className="text-[11px] font-medium text-emerald-300 bg-emerald-500/[0.08] border border-emerald-500/25 rounded-full px-2.5 py-0.5">
                      {cleanNotes}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
        </motion.div>

        {/* Week nav — glass morphic pill */}
        <div
          className="relative mb-5 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.35)] overflow-hidden"
          data-testid="week-nav"
        >
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5" />
          <div className="relative flex items-center justify-between px-3 py-3 sm:px-5">
            <button
              onClick={() => setWeek(Math.max(1, week - 1))}
              disabled={week === 1}
              className="group flex items-center gap-2 text-muted-foreground hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="prev-week-btn"
            >
              <span className="h-9 w-9 rounded-full border border-white/10 bg-white/[0.02] group-hover:border-primary/50 group-hover:bg-primary/10 flex items-center justify-center transition-all">
                <ChevronLeft className="h-4 w-4" />
              </span>
              <span className="hidden sm:inline text-sm font-medium">Previous</span>
            </button>

            <div className="flex flex-col items-center gap-1.5">
              <span className="font-heading text-white uppercase tracking-[0.2em] text-sm sm:text-base">
                Week <span className="text-primary">{week}</span> of {totalWeeks}
              </span>
              <div className="flex items-center gap-1.5">
                {Array.from({ length: totalWeeks }).map((_, idx) => (
                  <span
                    key={idx}
                    className={`h-1 rounded-full transition-all ${
                      idx + 1 === week
                        ? 'w-6 bg-primary shadow-[0_0_8px_rgba(250,204,21,0.6)]'
                        : idx + 1 < week
                        ? 'w-1.5 bg-primary/50'
                        : 'w-1.5 bg-white/15'
                    }`}
                  />
                ))}
              </div>
            </div>

            <button
              onClick={() => setWeek(Math.min(totalWeeks, week + 1))}
              disabled={week === totalWeeks}
              className="group flex items-center gap-2 text-muted-foreground hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="next-week-btn"
            >
              <span className="hidden sm:inline text-sm font-medium">Next</span>
              <span className="h-9 w-9 rounded-full border border-white/10 bg-white/[0.02] group-hover:border-primary/50 group-hover:bg-primary/10 flex items-center justify-center transition-all">
                <ChevronRight className="h-4 w-4" />
              </span>
            </button>
          </div>
        </div>

        {/* Focus chip */}
        {currentWeek?.focus && (
          <div
            className="mb-4 inline-flex items-start gap-2 rounded-full border border-primary/25 bg-primary/[0.06] px-3.5 py-1.5 max-w-full"
            data-testid="week-focus-chip"
          >
            <Target className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
            <span className="text-xs sm:text-sm text-muted-foreground leading-snug">
              <strong className="text-primary font-semibold">Focus · </strong>
              {currentWeek.focus}
            </span>
          </div>
        )}

        {/* Meals */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`week-${week}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.25 }}
            className="space-y-3"
          >
            {currentWeek?.sample_day?.map((meal, i) => {
              const fav = isMealFavorited(meal.foods);
              const isFavoritingThis = favoritingMeal === mealSignature(meal.foods);
              const hasRaw = typeof meal.raw_text === 'string' && meal.raw_text.trim().length > 0;
              const mealKey = (meal.meal || '').toLowerCase();
              const accent =
                mealKey.includes('break') ? 'bg-amber-400' :
                mealKey.includes('lunch') ? 'bg-emerald-400' :
                mealKey.includes('dinner') ? 'bg-sky-400' :
                mealKey.includes('snack') ? 'bg-fuchsia-400' :
                'bg-primary';
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: i * 0.04 }}
                  className="group relative rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-900/80 to-black/60 p-4 sm:p-5 shadow-[0_4px_20px_rgba(0,0,0,0.25)] hover:border-primary/30 hover:shadow-[0_8px_30px_rgba(250,204,21,0.08)] transition-all"
                  data-testid={`meal-card-${i}`}
                >
                  <div className="flex items-center justify-between mb-2.5">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className={`h-2 w-2 rounded-full ${accent} shadow-[0_0_8px_currentColor] flex-shrink-0`} />
                      <span className="font-heading text-sm text-white uppercase tracking-wider truncate">
                        {meal.meal}
                      </span>
                      {meal.swapped && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-green-300 bg-green-500/10 border border-green-500/30 rounded-full px-2 py-0.5">
                          Swapped
                        </span>
                      )}
                      {hasRaw && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary/10 border border-primary/30 rounded-full px-2 py-0.5">
                          Custom
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] font-medium text-muted-foreground bg-white/[0.03] border border-white/10 rounded-full px-2.5 py-0.5 flex-shrink-0">
                      {meal.time}
                    </span>
                  </div>

                  {hasRaw ? (
                    <p
                      className="text-white/90 text-sm leading-relaxed mb-3 whitespace-pre-wrap"
                      data-testid={`meal-raw-text-${i}`}
                    >
                      {meal.raw_text}
                    </p>
                  ) : (
                    <p className="text-white/90 text-sm leading-relaxed mb-3">{meal.foods}</p>
                  )}

                  {/* Macro chips */}
                  {hasRaw ? (
                    <p className="text-[11px] text-muted-foreground italic mb-3">
                      Macros hidden — edit to restore the AI version.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-white/70 bg-white/[0.04] border border-white/10 rounded-full px-2.5 py-1">
                        <Flame className="h-3 w-3 text-orange-400" /> {meal.calories} cal
                      </span>
                      <span className="text-[11px] font-medium text-red-300 bg-red-500/[0.08] border border-red-500/25 rounded-full px-2.5 py-1">
                        {meal.protein_g}g P
                      </span>
                      <span className="text-[11px] font-medium text-yellow-300 bg-yellow-500/[0.08] border border-yellow-500/25 rounded-full px-2.5 py-1">
                        {meal.carbs_g}g C
                      </span>
                      <span className="text-[11px] font-medium text-blue-300 bg-blue-500/[0.08] border border-blue-500/25 rounded-full px-2.5 py-1">
                        {meal.fats_g}g F
                      </span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 pt-2 border-t border-white/[0.06]">
                    <button
                      onClick={() => {
                        const preFilled = meal.raw_text
                          ? meal.raw_text
                          : `${meal.foods || ''} (~${meal.calories || 0} cal, ${meal.protein_g || 0}g P, ${meal.carbs_g || 0}g C, ${meal.fats_g || 0}g F)`;
                        setRawTextTarget({
                          weekIndex: week - 1,
                          mealIndex: i,
                          initialText: preFilled,
                          mealLabel: meal.meal,
                        });
                      }}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-primary bg-primary/[0.06] hover:bg-primary/[0.12] border border-primary/25 hover:border-primary/50 rounded-full px-3 py-2 transition-all"
                      title="Edit this meal — pre-filled with the AI suggestion so you can tweak, not retype"
                      data-testid={`edit-meal-text-btn-${i}`}
                    >
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                    <button
                      onClick={() => toggleFavorite(meal)}
                      disabled={isFavoritingThis || hasRaw}
                      className={`inline-flex items-center justify-center h-9 w-9 rounded-full border transition-all ${
                        hasRaw
                          ? 'opacity-30 cursor-not-allowed border-white/10 text-muted-foreground'
                          : fav
                          ? 'text-yellow-300 border-yellow-500/40 bg-yellow-500/[0.08] hover:bg-yellow-500/[0.15]'
                          : 'text-muted-foreground hover:text-yellow-300 border-white/10 hover:border-yellow-500/40 hover:bg-yellow-500/[0.06]'
                      }`}
                      title={hasRaw ? 'Revert to AI version to favorite this meal' : fav ? 'Remove favorite' : 'Save favorite'}
                      data-testid={`favorite-meal-btn-${i}`}
                    >
                      {isFavoritingThis ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Star className={`h-3.5 w-3.5 ${fav ? 'fill-current' : ''}`} />
                      )}
                    </button>
                    <button
                      onClick={() => { setSwapTarget({ weekIndex: week - 1, mealIndex: i, meal }); setSwapDialogOpen(true); }}
                      disabled={hasRaw}
                      className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-medium rounded-full px-3 py-2 border transition-all ${
                        hasRaw
                          ? 'opacity-30 cursor-not-allowed text-muted-foreground border-white/10'
                          : 'text-green-300 bg-green-500/[0.06] hover:bg-green-500/[0.12] border-green-500/25 hover:border-green-500/50'
                      }`}
                      title={hasRaw ? 'Revert to AI version to swap this meal' : 'Swap with an AI alternative'}
                      data-testid={`swap-meal-btn-${i}`}
                    >
                      <Shuffle className="h-3.5 w-3.5" /> Swap
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>

        <FavoriteMealsSection favorites={favorites} onRemove={removeFavorite} removing={removingFavorite} />

        {/* Grocery list — modernized */}
        {(() => {
          const normalizedCats = Array.isArray(groceryList?.categories)
            ? groceryList.categories.map((c, ci) => ({
                name: c.name || `Items ${ci + 1}`,
                items: c.items || [],
              }))
            : groceryList?.categories && typeof groceryList.categories === 'object'
            ? Object.entries(groceryList.categories).map(([name, items]) => ({
                name,
                items: Array.isArray(items) ? items : [],
              }))
            : null;
          const totalItems = normalizedCats
            ? normalizedCats.reduce((sum, c) => sum + (c.items?.length || 0), 0)
            : 0;

          return (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="mt-6 relative rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-900/80 to-black/60 shadow-[0_4px_20px_rgba(0,0,0,0.25)] overflow-hidden"
              data-testid="grocery-list-section"
            >
              <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-primary/[0.05] to-transparent" />

              <div className="relative p-5 sm:p-6">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="h-10 w-10 rounded-full bg-primary/10 border border-primary/25 flex items-center justify-center flex-shrink-0">
                      <ShoppingCart className="h-5 w-5 text-primary" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="font-heading text-white uppercase tracking-wider text-base sm:text-lg leading-tight flex items-center gap-2 flex-wrap">
                        Grocery List
                        {groceryList?.stale && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-300 bg-amber-500/[0.1] border border-amber-500/40 rounded-full px-2 py-0.5" data-testid="grocery-stale-badge">
                            Out of date
                          </span>
                        )}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-[11px] font-medium text-muted-foreground bg-white/[0.03] border border-white/10 rounded-full px-2 py-0.5">
                          Week {week}
                        </span>
                        {normalizedCats && (
                          <span className="text-[11px] font-medium text-primary bg-primary/[0.08] border border-primary/25 rounded-full px-2 py-0.5">
                            {totalItems} items · {normalizedCats.length} categories
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={generateGrocery}
                    disabled={generatingGrocery}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-2 transition-all flex-shrink-0 disabled:opacity-60 ${
                      groceryList?.stale
                        ? 'text-black bg-amber-400 hover:bg-amber-300 shadow-[0_4px_14px_rgba(251,191,36,0.35)] animate-pulse'
                        : 'text-black bg-primary hover:bg-primary/90 shadow-[0_4px_14px_rgba(250,204,21,0.25)]'
                    }`}
                    data-testid="generate-grocery-btn"
                  >
                    {generatingGrocery ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Working…
                      </>
                    ) : (
                      groceryList ? (groceryList.stale ? 'Update Now' : 'Regenerate') : 'Generate'
                    )}
                  </button>
                </div>

                {/* Stale banner — full-width alert when meals have changed */}
                {groceryList?.stale && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-3 flex items-start gap-2.5"
                    data-testid="grocery-stale-banner"
                  >
                    <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-100/90 leading-relaxed">
                      <span className="font-semibold text-amber-300">Meals changed since this list was generated.</span>{' '}
                      Tap <span className="font-semibold">Update Now</span> to rebuild the list to match your latest swaps and edits.
                    </p>
                  </motion.div>
                )}

                {normalizedCats ? (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {normalizedCats.map((cat, ci) => (
                      <motion.div
                        key={cat.name || `cat-${ci}`}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25, delay: ci * 0.04 }}
                        className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3.5"
                      >
                        <div className="flex items-center gap-2 mb-2.5 pb-2 border-b border-white/[0.06]">
                          <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_6px_currentColor]" />
                          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">
                            {cat.name}
                          </p>
                          <span className="ml-auto text-[10px] font-medium text-muted-foreground bg-white/[0.03] rounded-full px-1.5 py-0.5">
                            {cat.items.length}
                          </span>
                        </div>
                        <ul className="space-y-1.5">
                          {cat.items.map((it, i) => (
                            <li
                              key={`${cat.name || ci}-${it.item || it.name || i}`}
                              className="flex items-baseline gap-2 text-sm text-white/90"
                            >
                              <span className="text-primary/60 text-xs mt-0.5">▸</span>
                              <div className="flex-1 min-w-0">
                                <span className="text-white/90">{it.item || it.name}</span>
                                {it.quantity && (
                                  <span className="text-muted-foreground text-xs ml-1.5">
                                    · {it.quantity}
                                  </span>
                                )}
                                {it.notes && (
                                  <span className="block text-muted-foreground text-[11px] italic pl-3 mt-0.5">
                                    {it.notes}
                                  </span>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-6 text-center">
                    <ShoppingCart className="h-8 w-8 text-primary/40 mx-auto mb-2" />
                    <p className="text-white/90 text-sm font-medium">No grocery list yet</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      Generate a weekly shopping list built from this plan's meals.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })()}
      </main>

      <MealSwapDialog
        isOpen={swapDialogOpen}
        onClose={() => { setSwapDialogOpen(false); setSwapTarget(null); }}
        api={api}
        planId={plan?.id}
        weekIndex={swapTarget?.weekIndex}
        mealIndex={swapTarget?.mealIndex}
        originalMeal={swapTarget?.meal || {}}
        onSwapApplied={(updatedPlan) => setPlan(updatedPlan)}
        favorites={favorites}
        onFavoriteAdded={(fav) => setFavorites((prev) => (prev.some((f) => f.id === fav.id) ? prev : [fav, ...prev]))}
      />

      <MealRawTextEditorDialog
        open={rawTextTarget !== null}
        onClose={() => setRawTextTarget(null)}
        api={api}
        planId={plan?.id}
        weekIndex={rawTextTarget?.weekIndex}
        mealIndex={rawTextTarget?.mealIndex}
        initialText={rawTextTarget?.initialText || ''}
        mealLabel={rawTextTarget?.mealLabel || ''}
        onSaved={(updatedPlan) => setPlan(updatedPlan)}
      />
    </div>
  );
};
